import type {FeatureAppEnvironment, FeatureServices} from '@feature-hub/core';
import type {HistoryServiceV3} from '@feature-hub/history-service';
import type {AdbContextServiceV2} from '@smart/adb-context-service';
import type {EndpointDirectoryV3} from '@smart/endpoint-directory';
import type {LocaleServiceV3} from '@smart/locale-service';
import type {
  LoginServiceV1,
  LoginServiceV2,
  LoginServiceV3,
} from '@smart/login-service';
import type {PageContextServiceV1} from '@smart/page-context-service';
import type {PaymentConfigServiceV1} from '@smart/payment-config-service';
import type {PricePreferencesProviderV1} from '@smart/price-preferences-provider';
import type {PubSubServiceV1} from '@smart/pub-sub-service';
import type {SelectedVehicleServiceV1} from '@smart/selected-vehicle-service';
import * as React from 'react';
import type {ReactNode} from 'react';

export interface FeatureAppDependencies extends FeatureServices {
  readonly 'adb-context-service': AdbContextServiceV2;
  readonly 'endpoint-directory': EndpointDirectoryV3;
  readonly 'locale-service': LocaleServiceV3;
  readonly 'login-service': LoginServiceV1 | LoginServiceV2 | LoginServiceV3;
  readonly 'page-context-service': PageContextServiceV1;
  readonly 'payment-config-service': PaymentConfigServiceV1;
  readonly 'price-preferences-provider': PricePreferencesProviderV1;
  readonly 'pub-sub': PubSubServiceV1;
  readonly 's2:history': HistoryServiceV3;
  readonly 'selected-vehicle-service': SelectedVehicleServiceV1;
}

export type FeatureAppEnv = FeatureAppEnvironment<FeatureAppDependencies, {}>;

export const FeatureAppEnvContext = React.createContext<
  FeatureAppEnv | undefined
>(undefined);

export interface FeatureAppEnvProviderProps {
  children: ReactNode;
  env: FeatureAppEnv;
}

export const FeatureAppEnvProvider = ({
  children,
  env,
}: FeatureAppEnvProviderProps) => {
  return (
    <FeatureAppEnvContext.Provider value={env}>
      {children}
    </FeatureAppEnvContext.Provider>
  );
};

export function useFeatureAppEnv<
  Config = {},
  TFeatureServices extends FeatureServices = FeatureAppDependencies,
>(): FeatureAppEnvironment<TFeatureServices, Config> {
  const env = React.useContext(FeatureAppEnvContext);

  if (!env) {
    throw new Error(`FeatureAppEnvContext provider is missing.`);
  }

  return env as unknown as FeatureAppEnvironment<TFeatureServices, Config>;
}
